import { useState, useMemo } from 'react';
import { Modal, Backdrop, Box, Typography, Switch } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';

import { useTextStyles } from 'src/hooks/useTextStyles';
import Button from 'src/components/NewButton';
import { ReactComponent as CampaignsIcon } from 'src/assets/icons/campaigns.svg';
import TextInput from 'src/components/TextInput';
import { ReactComponent as AlertIcon } from 'src/assets/icons/alert.svg';
import { createCampaign } from 'src/apis/campaign';
import useNotification from 'src/hooks/useNotification';
import useCampaign from 'src/hooks/useCampaign';
import { Preference } from 'src/types/preference';
import { PreferencesModal } from 'src/modals/PreferencesModal';

import { ImportTable } from './ImportTable';

const REQUIRED_IMPORT_COLUMNS = [
  'Show Title',
  'Episode Identifier',
  'Impression Count',
  'View Date',
] as const;

interface CampaignModalProps {
  open: boolean;
  closeModal: () => void;
}

const useStyles = makeStyles(() =>
  createStyles({
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 16,
    },
    content: {
      width: '100%',
      height: 500,
      marginTop: 20,
      overflowY: 'auto',
    },
    buttons: {
      paddingTop: 16,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      '& button': {
        flex: 1,
      },
    },
    warning: {
      borderRadius: 28,
      border: '8px solid #FFFAEB',
      background: '#FEF0C7',
    },
    confirm: {
      borderRadius: 28,
      border: '8px solid #ECFDF3',
      background: '#DCFAE6',
    },
    confirmModalContent: {
      marginTop: 16,
      marginBottom: 24,
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
    },
    confirmBtn: {
      flex: 1,
    },
    logo: {
      width: 50,
      height: 50,
      padding: 12,
      borderRadius: 8,
      border: '1px solid var(--Gray-200, #EAECF0)',
      background: '#FFF',
      boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
    },
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      height: '100%',
    },
    inputField: {
      display: 'flex',
      flexDirection: 'column',
      columnGap: 6,
    },
    description: {
      padding: '6px 8px',
      borderRadius: 8,
      border: '1px solid #C3C3C7',
      width: '100%',

      '& .MuiInputBase-root:before, & .MuiInputBase-root:after': {
        border: 'none !important',
      },
    },
    dropdown: {
      padding: '10px 14px',
      borderRadius: 8,
      width: '100%',
      height: '52px',
      marginTop: 4,
      justifyContent: 'flex-start',
      '& p': {
        fontSize: 16,
        fontWeight: 400,
      },
    },
    fieldsWarning: {
      padding: '8px 12px',
      border: '1px solid #FEC84B',
      borderRadius: 8,
      boxShadow: '0px 1px 2px 0px #1018280D',
      backgroundColor: '#FEF0C7',
    },
    toggleContainerWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    toggleContainer: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
    },
    buttonRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 0,
    },
  })
);

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '100%',
  maxWidth: 1000,
  bgcolor: 'background.paper',
  background: '#fff',
  borderRadius: 8,
  boxShadow:
    '0px 8px 8px -4px rgba(16, 24, 40, 0.03), 0px 20px 24px -4px rgba(16, 24, 40, 0.08)',
  padding: 24,
};

export const CampaignModal = ({ open, closeModal }: CampaignModalProps) => {
  const classes = useStyles();
  const textClasses = useTextStyles();

  const [importedData, setImportedData] = useState<Record<string, string>[]>(
    []
  );
  const [campaignFile, setCampaignFile] = useState<File | null>(null);
  const [step, setStep] = useState(0);
  const [hasImportErr, setHasImportErr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState<string>('');
  const [selectedPreference, setSelectedPreference] =
    useState<Preference | null>(null);
  const [preferencesModalOpen, setPreferencesModalOpen] = useState(false);
  const [description, setDescription] = useState<string>('');
  const [alignmentType, setAlignmentType] = useState<'show' | 'show_episode'>(
    'show'
  );
  const [publisher, setPublisher] = useState('');
  const [campaignStart, setCampaignStart] = useState('');
  const [campaignEnd, setCampaignEnd] = useState('');
  const { campaigns, setCampaigns, setSelectedCampaignId } = useCampaign();

  const { handleError, handleSuccess } = useNotification();

  const { missingImportFields, hasFieldsErr } = useMemo(() => {
    const requiredColumns = selectedPreference?.date_column_override
      ? REQUIRED_IMPORT_COLUMNS.filter((c) => c !== 'View Date')
      : [...REQUIRED_IMPORT_COLUMNS];

    const first = importedData[0];
    if (!first) {
      return {
        missingImportFields: [...requiredColumns],
        hasFieldsErr: true,
      };
    }
    const keys = Object.keys(first);
    const missing = requiredColumns.filter((col) => !keys.includes(col));
    return {
      missingImportFields: missing,
      hasFieldsErr: missing.length > 0,
    };
  }, [importedData, selectedPreference?.date_column_override]);

  const handlePrevious = () => {
    if (step === 0) {
      initModal();
    } else {
      setImportedData([]);
      setStep(step - 1);
    }
  };

  const handleNext = async () => {
    if (loading) {
      return;
    }

    if (step === 0) {
      if (
        name &&
        description &&
        publisher &&
        importedData &&
        importedData.length
      ) {
        setHasImportErr(false);
        setStep(1);
      } else {
        setHasImportErr(true);
      }
    } else {
      if (hasFieldsErr) {
        return;
      }
      if (!campaignFile) {
        handleError({}, 'Please upload a campaign file');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await createCampaign({
          name,
          preferenceId: selectedPreference?.id || '',
          description: description || undefined,
          alignmentRequired: alignmentType === 'show_episode',
          file: campaignFile,
          publisher,
          campaignStart: campaignStart || undefined,
          campaignEnd: campaignEnd || undefined,
          processingConfig: {
            episodeMatchingEnabled: alignmentType === 'show_episode',
          },
        });
        setCampaigns([response, ...campaigns]);
        setSelectedCampaignId(response.id);
        handleSuccess(`Successfully created ${name} campaign`);
        setLoading(false);
        initModal();
        closeModal();
      } catch (error) {
        handleError(error, 'There was an error while creating the campaign');
        setLoading(false);
      }
    }
  };

  const initModal = () => {
    setImportedData([]);
    setCampaignFile(null);
    setStep(0);
    setHasImportErr(false);
    setLoading(false);
    setName('');
    setDescription('');
    setPublisher('');
    setCampaignStart('');
    setCampaignEnd('');
    setSelectedPreference(null);
    setAlignmentType('show');
    setPreferencesModalOpen(false);
  };

  const handleCloseModal = () => {
    initModal();
    closeModal();
  };

  return (
    <>
      <Modal
        open={open}
        aria-labelledby='self-scoring-modal-title'
        aria-describedby='self-scoring-modal-description'
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 800,
        }}
      >
        <Box sx={{ ...modalStyle }}>
          <Box className={classes.header}>
            <Box className={classes.logo}>
              <CampaignsIcon
                fill='none'
                stroke='#344054'
                strokeWidth={1}
                strokeLinecap='round'
                strokeLinejoin='round'
                style={{ width: 24, height: 24 }}
              />
            </Box>
            <CancelIcon
              cursor='pointer'
              width={24}
              height={24}
              onClick={handleCloseModal}
            />
          </Box>
          <Box display='flex' flexDirection='column'>
            <Typography
              className={textClasses.lgBold}
              style={{ color: '#101828' }}
            >
              Create Campaign{' '}
              <span style={{ fontSize: 14, fontWeight: 400 }}>
                {step + 1} of 2
              </span>
            </Typography>
            {step === 1 && (
              <Typography className={textClasses.smRegular}>
                Confirm that the following fields are mapped to the correct
                column content, and adjust or verify data as necessary.
              </Typography>
            )}
          </Box>
          <Box className={classes.content}>
            <Box className={classes.container}>
              {step === 0 && (
                <Box display='flex' gridColumnGap='20px'>
                  <Box flex='1' className={classes.inputField}>
                    <Typography variant='body1' style={{ fontSize: 14 }}>
                      Campaign Name
                    </Typography>
                    <TextInput
                      style={
                        step === 0 && hasImportErr && !name
                          ? {
                              border: '1px solid #B42318',
                            }
                          : undefined
                      }
                      default='Enter name'
                      value={name}
                      onChange={(name) => setName(name)}
                      className={classes.description}
                    />
                  </Box>
                  <Box className={classes.inputField}>
                    <Typography variant='body1' style={{ fontSize: 14 }}>
                      Preference
                    </Typography>
                    <Box className={classes.buttonRow}>
                      <Button
                        variant='outlined'
                        height={46}
                        onClick={() => setPreferencesModalOpen(true)}
                        className={classes.dropdown}
                        style={{
                          justifyContent: 'flex-start',
                          padding: '6px 8px',
                          flex: 1,
                          height: 46,
                        }}
                      >
                        <Typography
                          style={{
                            fontSize: 16,
                            fontWeight: 400,
                            color: selectedPreference ? '#344054' : '#667085',
                          }}
                        >
                          {selectedPreference?.name || 'Select preference'}
                        </Typography>
                      </Button>
                    </Box>
                  </Box>
                  <Box className={classes.toggleContainerWrapper}>
                    <Box className={classes.toggleContainer}>
                      <Switch
                        checked={alignmentType === 'show_episode'}
                        onChange={(e) => {
                          setAlignmentType(
                            e.target.checked ? 'show_episode' : 'show'
                          );
                        }}
                        color='primary'
                      />
                      <Typography
                        style={{
                          fontSize: 16,
                          fontWeight: 400,
                          color: '#344054',
                        }}
                      >
                        {alignmentType === 'show'
                          ? 'Show Alignment'
                          : 'Show & Episode Alignment'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              {step === 0 && (
                <Box className={classes.inputField}>
                  <Typography variant='body1' style={{ fontSize: 14 }}>
                    Description
                  </Typography>
                  <TextInput
                    style={
                      step === 0 && hasImportErr && !description
                        ? {
                            border: '1px solid #B42318',
                          }
                        : undefined
                    }
                    default='Enter description'
                    value={description}
                    onChange={(value) => setDescription(value)}
                    className={classes.description}
                  />
                </Box>
              )}
              {step === 0 && (
                <Box display='flex' gridColumnGap='20px'>
                  <Box flex='1' className={classes.inputField}>
                    <Typography variant='body1' style={{ fontSize: 14 }}>
                      Publisher
                    </Typography>
                    <TextInput
                      style={
                        step === 0 && hasImportErr && !publisher
                          ? {
                              border: '1px solid #B42318',
                            }
                          : undefined
                      }
                      default='Enter publisher'
                      value={publisher}
                      onChange={(v) => setPublisher(v)}
                      className={classes.description}
                    />
                  </Box>
                  <Box flex='1' className={classes.inputField}>
                    <Typography variant='body1' style={{ fontSize: 14 }}>
                      Campaign start (optional)
                    </Typography>
                    <TextInput
                      type='date'
                      value={campaignStart}
                      onChange={(v) => setCampaignStart(v)}
                      className={classes.description}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                  <Box flex='1' className={classes.inputField}>
                    <Typography variant='body1' style={{ fontSize: 14 }}>
                      Campaign end (optional)
                    </Typography>
                    <TextInput
                      type='date'
                      value={campaignEnd}
                      onChange={(v) => setCampaignEnd(v)}
                      className={classes.description}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                </Box>
              )}
              {step === 1 && hasFieldsErr && (
                <Box
                  display='flex'
                  gridColumnGap='8px'
                  className={classes.fieldsWarning}
                >
                  <AlertIcon stroke='#DC6803' width={20} height={20} />
                  <Box display='flex' flexDirection='column' style={{ gap: 4 }}>
                    <Typography
                      className={textClasses.smBold}
                      style={{ color: '#B54708' }}
                    >
                      There are some fields that might be incorrectly mapped
                    </Typography>
                    <Typography
                      className={textClasses.smRegular}
                      style={{ color: '#93370D' }}
                      component='div'
                    >
                      Missing or unrecognized columns:{' '}
                      {missingImportFields.join(', ')}
                    </Typography>
                  </Box>
                </Box>
              )}
              <ImportTable
                data={importedData}
                setData={setImportedData}
                setFile={setCampaignFile}
                step={step}
                hasImportErr={hasImportErr && !importedData?.length}
              />
            </Box>
          </Box>
          <Box className={classes.buttons}>
            <Button
              title={step === 0 ? 'Cancel' : 'Previous'}
              variant='outlined'
              height={46}
              onClick={handlePrevious}
            />
            <Button height={46} onClick={handleNext}>
              {loading ? (
                <CircularProgress style={{ color: '#fff' }} size={24} />
              ) : (
                <span>{step === 1 ? 'Create Campaign' : 'Import'}</span>
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
      <PreferencesModal
        open={preferencesModalOpen}
        closeModal={() => setPreferencesModalOpen(false)}
        onSelect={(preference) => {
          setSelectedPreference(preference);
          setPreferencesModalOpen(false);
        }}
        selectedPreferenceId={selectedPreference?.id || null}
        alignmentType={alignmentType}
      />
    </>
  );
};
